import DOMPurify from 'dompurify';
import twemoji from 'twemoji';
import { useMemo } from 'react';

const URL_SPLIT = /(https?:\/\/[^\s<]+[^<.,:;"')\]\s])/g;

function escapeHtml(s: string) {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function linkifyPlainText(raw: string) {
  const parts = raw.split(URL_SPLIT);
  return parts
    .map((part) => {
      if (/^https?:\/\//.test(part)) {
        const safe = escapeHtml(part);
        return `<a href="${safe}" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline break-all">${safe}</a>`;
      }
      return escapeHtml(part);
    })
    .join('');
}

type Props = {
  text: string;
  className?: string;
};

/**
 * Plain text → escape + autolink → Twemoji (SVG) → DOMPurify.
 */
export function StaffMessageBody({ text, className }: Props) {
  const html = useMemo(() => {
    const linked = linkifyPlainText(text);
    const withEmoji = twemoji.parse(linked, {
      folder: 'svg',
      ext: '.svg',
      className: 'inline-block h-[1.1em] w-[1.1em] align-[-0.15em] mx-px',
    });
    return DOMPurify.sanitize(withEmoji, {
      ADD_ATTR: ['target', 'rel', 'class'],
      ALLOWED_TAGS: ['a', 'img', 'span'],
      ALLOWED_ATTR: ['href', 'class', 'src', 'alt', 'loading', 'draggable', 'target', 'rel'],
    });
  }, [text]);

  return (
    <span
      className={className}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
